import type { FactoryProduct } from '@/models';
import type { ProductRosterDetail } from '@/models/ProductRosterDetail';
import type {
  CustomTextConfig,
  ProductionComposeInput,
} from '@/composables/useProductionFileEngine';
import { visibleCustomLogos } from './logoScope';

type RosterRole = 'name' | 'number';

/**
 * Which roster value a saved text takes — the builder's rule
 * (`linkedRoleOf`, Clone of builder v2 `src/lib/linkedTakeoverTexts.ts`):
 *  - a design takeover text: its `takeover_role`;
 *  - any other design text: none;
 *  - an explicit `linked_template_role`;
 *  - every configured (not customer-added) number — dual-number products
 *    (baseball: front + back) have several, all showing the roster number;
 *  - the configured name flagged `is_first_name`. Other `type: 'name'` texts
 *    (team name, captains, fixed text) keep the value the customer typed.
 */
function rosterRole(text: any): RosterRole | null {
  const fromDesign = text?.design_id !== null && text?.design_id !== undefined;
  const isTakeover = text?.manually_added === true && fromDesign;
  if (isTakeover) {
    return text.takeover_role === 'name' || text.takeover_role === 'number'
      ? text.takeover_role
      : null;
  }
  if (fromDesign) return null;
  if (text?.linked_template_role === 'name' || text?.linked_template_role === 'number') {
    return text.linked_template_role;
  }
  if (!text?.manually_added && text?.type === 'number') return 'number';
  if (!text?.manually_added && text?.type === 'name' && text?.is_first_name) return 'name';
  return null;
}

/**
 * Indexes of the texts that show the roster's value for `role`. Orders saved
 * before the flags existed carry none of them: then the first text of that
 * type, as before.
 */
export function rosterTextIndexes(texts: any[], role: RosterRole): number[] {
  const linked = texts.flatMap((t, i) => (rosterRole(t) === role ? [i] : []));
  if (linked.length) return linked;
  const first = texts.findIndex((t) => t?.type === role);
  return first === -1 ? [] : [first];
}

/**
 * Map an order line (factory product) + one roster row to a compose input,
 * baking the row's name/number into the product's roster text slots —
 * the same mapping the per-row "Download SVG" flow uses.
 */
export function buildComposeInputForRow(
  product: FactoryProduct,
  row: ProductRosterDetail
): ProductionComposeInput {
  const customTexts: CustomTextConfig[] = product.product_custom_texts
    ? JSON.parse(JSON.stringify(product.product_custom_texts))
    : [];

  for (const i of rosterTextIndexes(customTexts, 'name')) customTexts[i].value = row.text ?? '';
  for (const i of rosterTextIndexes(customTexts, 'number')) {
    customTexts[i].value = String(row.number ?? '');
  }

  return {
    designUrl: product.production_design_url ?? '',
    sizesUrl: product.design_sizes_url || undefined,
    safeZoneUrl: product.design_safezone_url || undefined,
    size: row.size ?? '',
    groupPatterns: Array.isArray(product.group_patterns) ? {} : (product.group_patterns ?? {}),
    // Only the logos the customer saw on this design (logoScope.ts).
    custom_logos: visibleCustomLogos(product),
    texts: customTexts,
    svgGroups: product.svg_groups ?? [],
    logos: product.fixed_logos ?? [],
  };
}
